import { useEffect, useRef } from 'react'

import { LogTrainingContentExitDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { buildUrl } from 'modules/core/lib/buildUrl'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

const showError = () => app.setFlash({ type: 'wrong', message: i18n.t('common.error') })

type UseTrainingContentExitLoggerOptions = {
  trainingId: number | string | undefined
  /** Whether exit logging is enabled. Pass from checkFeatures (when you have query data) or useFeatures to avoid extra request. */
  enabled: boolean
}

/**
 * Custom hook to automatically log training content exit events
 *
 * Handles exit logging for all navigation scenarios:
 * - Close tab/window
 * - Close browser
 * - Browser back button
 * - React Router navigation
 * - Type new URL
 *
 * Does NOT show confirmation dialogs - silently logs exits
 *
 * @param options.trainingId - The training ID to log exit for
 * @param options.enabled - Whether exit logging is enabled (from checkFeatures or useFeatures)
 *
 * @returns Object with logExit function for manual triggering
 *
 * @example
 * // When you already have features from a query (e.g. LoadContent):
 * const { has_dev_training_lms_connectivity_logs } = checkFeatures(['dev_training_lms_connectivity_logs'], data?.features?.featuresConnection?.nodes)
 * const { logExit } = useTrainingContentExitLogger({ trainingId, enabled: has_dev_training_lms_connectivity_logs })
 *
 * // When you don't have features in your query:
 * const { has_dev_training_lms_connectivity_logs } = useFeatures(['dev_training_lms_connectivity_logs'])
 * const { logExit } = useTrainingContentExitLogger({ trainingId, enabled: has_dev_training_lms_connectivity_logs })
 *
 * // Manual exit (e.g., exit button)
 * const handleExitClick = async () => {
 *   await logExit()
 *   navigate('/trainings')
 * }
 */
export const useTrainingContentExitLogger = ({
  trainingId,
  enabled,
}: UseTrainingContentExitLoggerOptions) => {
  const { employee: currentEmployee } = useCurrentSession()

  const [logExitMutation] = useMutation(LogTrainingContentExitDocument, {
    extractorFn(mutationData) {
      return mutationData?.trainingsMutations?.logExitTrainingsContentActivityLog
    },
  })

  // Track if exit has been logged to prevent duplicates
  const hasLoggedExit = useRef(false)

  /**
   * CONTROLLED exit handler - Used when we have time to wait for response
   * Use for: Exit button click, controlled navigation
   */
  const logExitAsync = async (): Promise<boolean> => {
    if (!enabled || !trainingId) return false
    if (hasLoggedExit.current) return true

    try {
      const result = await logExitMutation({
        variables: {
          trainingId: Number(trainingId),
          employeeId: currentEmployee.id,
        },
      })
      const handled = handleResult(result, showError)
      if (handled.success) {
        hasLoggedExit.current = true
      }
      return handled.success
    } catch {
      showError()
      return false
    }
  }

  /**
   * UNCONTROLLED exit handler - Fire-and-forget for page unload
   * Use for: Close tab, close window, close browser, type new URL
   */
  const logExitBeacon = () => {
    if (!enabled || !trainingId) return
    if (hasLoggedExit.current) return
    hasLoggedExit.current = true

    // Apollo Client appends operationName as query param
    // Match Apollo's behavior: buildUrl('/graphql') + '?LogTrainingContentExit'
    const operationName = 'LogTrainingContentExit'
    const url = `${buildUrl('/graphql')}?${operationName}`

    const payload = JSON.stringify({
      operationName,
      query: `mutation LogTrainingContentExit($trainingId: Int!, $employeeId: Int!) {
  trainingsMutations {
    logExitTrainingsContentActivityLog(trainingId: $trainingId, employeeId: $employeeId) {
      contentActivityLog {
        id
      }
      errors {
        ... on SimpleError {
          message
          type
        }
        ... on StructuredError {
          field
          messages
        }
      }
    }
  }
}`,
      variables: {
        trainingId: Number(trainingId),
        employeeId: currentEmployee.id,
      },
    })

    // Use sendBeacon - designed for reliable page unload requests
    // Browser guarantees delivery even after page closes
    const blob = new Blob([payload], { type: 'application/json' })
    navigator.sendBeacon(url, blob)
  }

  // Setup exit logging for all navigation scenarios
  useEffect(() => {
    if (!enabled || !trainingId) return

    // Reset flag when training changes
    hasLoggedExit.current = false

    /**
     * Handle: Close tab, Close window, Close browser, Type new URL
     * Uses sendBeacon for guaranteed delivery during page unload
     */
    const handleBeforeUnload = () => {
      logExitBeacon()
    }

    /**
     * Handle: Browser back button
     * Try async first (we have ~100ms), fallback to beacon
     */
    const handlePopState = async () => {
      if (!hasLoggedExit.current) {
        const success = await logExitAsync()
        // If async failed, beacon will be called on unmount
        if (!success && !hasLoggedExit.current) {
          logExitBeacon()
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    // Cleanup: Component unmount (React Router navigation, programmatic navigation)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)

      // On unmount, ensure exit is logged
      if (!hasLoggedExit.current) {
        logExitBeacon()
      }
    }
  }, [enabled, trainingId, currentEmployee.id])

  return {
    /**
     * Manually trigger exit log (for exit button, etc.)
     * Returns promise that resolves to true if successful
     */
    logExit: logExitAsync,
  }
}
